import React, { Fragment, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView, usePathParam, useQueryParam, useViewport } from '@chilecompra/react-kit'
import { Button, Skeleton, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import { Grid } from '@mui/material'
import {
  ABILITY_STATUS_TYPES,
  ANTIUNION_PRACTICES,
  CERTIFICATE_SCHEDULED_MILLISECONDS,
  DISABILITY_REASONS
} from '../../config/settings/constant'
import { PUBLIC_URL } from '../../config/settings/environment'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import {
  onGetSellerEnrollmentCertificateThunk,
  onGetTenderEnrollmentCertificateThunk,
  onScheduledEnrollmentCertificateThunk
} from './EnrollmentCertificateSection.actions'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import {
  AbilityReasonError,
  AbilityTag,
  Content,
  ErrorIcon,
  MainText,
  Root,
  Table,
  TableCell,
  TableWrapper,
  TableRowWrapper,
  StyledImage,
  InfoWrapper,
  Label,
  Value,
  StatusToast,
  Strong,
  SeparationRow
} from './EnrollmentCertificateSection.styles'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'

/**
 * The enrollment certificate section container.
 */
const EnrollmentCertificateSection = () => {
  const dispatch = useDispatch()
  const {
    size: { isTiny, isSmall }
  } = useViewport()
  const numAcquisition = useQueryParam('numAdquisicion')
  const dni = usePathParam('rut')
  const { userType } = useAuthorization()
  const {
    certificate: {
      awardDate,
      openingDate,
      disabilityReasons,
      completed,
      failed,
      opening,
      adjudication,
      tenderID,
      temporaryUnions,
      sellers
    },
    error,
    loading,
    scheduled
  } = useSelector(state => state.enrollmentCertificate)

  // Case to show the group certificate from a tender
  useEffect(() => {
    if (!loading || !(numAcquisition !== null)) {
      return
    }

    dispatch(onGetTenderEnrollmentCertificateThunk({ numAcquisition }))
  }, [numAcquisition])

  // Case to show the individual certificate
  useEffect(() => {
    if (!loading || !(dni !== undefined)) {
      return
    }

    dispatch(onGetSellerEnrollmentCertificateThunk({ dni, userType }))
  }, [dni, userType])

  // Button to reload information
  const handleClickReload = event => {
    event.preventDefault()

    if (numAcquisition) {
      dispatch(onGetTenderEnrollmentCertificateThunk({ numAcquisition }))
    }
  }

  // Even if user doesn't press the reload button, we schedule the execution for 1 more minute
  // This is only applicable when the certificate has not yet been generated
  const scheduledDispatch = useCallback(() => {
    if (numAcquisition && !error && !loading && !completed && !scheduled) {
      dispatch(onScheduledEnrollmentCertificateThunk())
      return setTimeout(
        () => dispatch(onGetTenderEnrollmentCertificateThunk({ numAcquisition })),
        CERTIFICATE_SCHEDULED_MILLISECONDS
      )
    }
  }, [numAcquisition, error, loading, completed, scheduled])

  scheduledDispatch()
  useAnalyticsPageView('/ficha/certificado')

  return (
    <Root>
      {error && !loading && (
        <Toast
          border
          textDirection="vertical"
          title="Lo sentimos, estamos presentando problemas con nuestro servidor"
          severity="error"
        >
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}

      {!error && !loading && completed && failed && (
        <Toast
          border
          textDirection="vertical"
          title="Lo sentimos, en este momento estamos generando, o no contamos, con la información solicitada"
          severity="error"
        >
          Por favor, consulta en unos minutos más.
        </Toast>
      )}

      {!error && !loading && !completed && (
        <Content textAlign="center">
          <Toast border title="Importante:" severity="info">
            Esta información esta siendo generada. Por favor, espere unos minutos.
          </Toast>
          <Button color="primary" margin="48px 0 0" onClick={handleClickReload}>
            Volver a cargar
          </Button>
        </Content>
      )}

      {!error && !loading && completed && !failed && numAcquisition && (
        <StatusToast severity="success" disableIcon border margin="10px 0">
          <Grid container justifyContent="center" alignItems="center">
            <InfoWrapper item xs={4}>
              <Label variant="body2">ID de licitación:</Label>
              <Value variant="body1">{tenderID}</Value>
            </InfoWrapper>
            <InfoWrapper item xs={4}>
              <Label variant="body2">Fecha de la apertura:</Label>
              <Value variant="body1">{openingDate || '-'}</Value>
            </InfoWrapper>
            <InfoWrapper item xs={4}>
              <Label variant="body2">Fecha de la adjudicación:</Label>
              <Value variant="body1">{awardDate || '-'}</Value>
            </InfoWrapper>
          </Grid>
        </StatusToast>
      )}

      {!error && loading && (
        <>
          <Skeleton width="100%" />
          <Skeleton width="50%" />
        </>
      )}

      {!error &&
        !loading &&
        completed &&
        !failed &&
        (numAcquisition ? (
          <Typography variant="h4" margin="30px 0 0">
            <MainText>
              Se certifica que el proveedor indicado a continuación poseen el siguiente estado de habilidad para ofertar
              y contratar, de acuerdo con la fecha y hora especificada.
            </MainText>
          </Typography>
        ) : (
          <Typography variant="h4">
            Se certifica que el proveedor indicado a continuación poseen el siguiente estado de habilidad para ofertar y
            contratar, de acuerdo con la fecha y hora especificada.
          </Typography>
        ))}

      {loading && !error && (
        <>
          <TablePlaceholder margin="9px 0 0" />
        </>
      )}

      {!error &&
        completed &&
        !failed &&
        dni !== undefined &&
        (numAcquisition ? (
          <TableWrapper>
            <Typography variant="body1" margin="15px 0 40px">
              <Strong>Proveedores individuales</Strong>
            </Typography>
            <Table accessibility={{ label: 'Estado de inscripción' }} loading={loading}>
              <TableRow>
                <TableCell disableBottomBorder padding="0 9px 6px 0">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    Proveedor
                  </Typography>
                </TableCell>
                <TableCell disableBottomBorder padding="0 9px 6px" width="250px">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    Tipo de Proveedor
                  </Typography>
                </TableCell>
                <TableCell disableBottomBorder padding="0 9px 6px" width="250px">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    RUT/ID
                  </Typography>
                </TableCell>
                <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    Estado
                  </Typography>
                </TableCell>
              </TableRow>
              {sellers?.map((seller, index) => {
                return (
                  <TableRow key={`row-${index}`}>
                    <TableCell disableBottomBorder padding="6px 9px 6px 0">
                      <Typography variant="body2">{seller.orgLegalName}</Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 9px 6px 0">
                      <Typography variant="body2">{seller.typeSeller}</Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 9px 6px" width="250px">
                      <Typography variant="body2">{seller.dni}</Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                      <AbilityTag
                        color={ABILITY_STATUS_TYPES[seller.ability.status]?.color}
                        enableIcon
                        padding="0"
                        text={ABILITY_STATUS_TYPES[seller.ability.status]?.label}
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </Table>
          </TableWrapper>
        ) : (
          <TableWrapper>
            <Table accessibility={{ label: 'Estado de inscripción' }} loading={loading}>
              <TableRow>
                <TableCell disableBottomBorder padding="0 9px 6px 0">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    Proveedor
                  </Typography>
                </TableCell>
                <TableCell disableBottomBorder padding="0 9px 6px 0">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    Tipo de Proveedor
                  </Typography>
                </TableCell>
                <TableCell disableBottomBorder padding="0 9px 6px" width="150px">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    RUT/ID
                  </Typography>
                </TableCell>
                <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                  <Typography color="black3" fontWeight="bold" variant="body1">
                    Estado
                  </Typography>
                </TableCell>
              </TableRow>
              {sellers?.map((seller, index) => {
                return (
                  <TableRow key={`row-${index}`}>
                    <TableCell disableBottomBorder padding="6px 9px 6px 0">
                      <Typography fontWeight="bold" variant="body2">
                        {seller.orgLegalName}
                      </Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 9px 6px 0">
                      <Typography fontWeight="bold" variant="body2">
                        {seller.typeSeller}
                      </Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 9px 6px" width="150px">
                      <Typography fontWeight="bold" variant="body2">
                        {seller.dni}
                      </Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                      <AbilityTag
                        color={ABILITY_STATUS_TYPES[seller.ability.status]?.color}
                        enableIcon
                        padding="0"
                        text={ABILITY_STATUS_TYPES[seller.ability.status]?.label}
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </Table>
          </TableWrapper>
        ))}

      {!error && completed && !failed && dni !== undefined && numAcquisition && temporaryUnions?.length > 0 && (
        <TableWrapper>
          <Table accessibility={{ label: 'Estado de inscripción' }} loading={loading}>
            <TableRow>
              <Typography variant="body1" margin="15px 0 40px">
                <Strong>Proveedores en UTP</Strong>
              </Typography>
            </TableRow>
            {temporaryUnions?.map((UTP, index) => (
              <Fragment key={UTP.idUTP}>
                <TableRow>
                  <TableCell disableBottomBorder padding={`${index !== 0 ? '30px' : '0'} 9px 6px 0`}>
                    <Typography color="black3" fontWeight="bold" variant="body1">
                      ID UTP
                    </Typography>
                  </TableCell>
                  <TableCell disableBottomBorder padding="0 9px 6px" width="250px" />
                  <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                    <Typography color="black3" fontWeight="bold" variant="body1">
                      Estado
                    </Typography>
                  </TableCell>
                </TableRow>
                <SeparationRow>
                  <TableCell disableBottomBorder padding="15px 0">
                    <Typography variant="body2">{UTP.idUTP}</Typography>
                  </TableCell>
                  <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px" />
                  <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                    <AbilityTag
                      color={ABILITY_STATUS_TYPES[UTP.status].color}
                      enableIcon
                      padding="0"
                      text={ABILITY_STATUS_TYPES[UTP.status].label}
                    />
                  </TableCell>
                </SeparationRow>
                <TableRow>
                  <TableCell disableBottomBorder padding="20px 9px 6px 0">
                    <Typography color="black3" fontWeight="bold" variant="body1">
                      Proveedor
                    </Typography>
                  </TableCell>
                  <TableCell disableBottomBorder padding="20px 9px 6px 0">
                    <Typography color="black3" fontWeight="bold" variant="body1">
                      Tipo de Proveedor
                    </Typography>
                  </TableCell>
                  <TableCell disableBottomBorder padding="0 9px 6px" width="250px">
                    <Typography color="black3" fontWeight="bold" variant="body1">
                      RUT/ID
                    </Typography>
                  </TableCell>
                  <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                    <Typography color="black3" fontWeight="bold" variant="body1">
                      Estado
                    </Typography>
                  </TableCell>
                </TableRow>

                {UTP.providers.map(provider => (
                  <TableRow key={dni}>
                    <TableCell disableBottomBorder padding="6px 9px 6px 0">
                      <Typography variant="body2">{provider.orgLegalName.toUpperCase()}</Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 9px 6px 0">
                      <Typography variant="body2">{provider.typeSeller}</Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 9px 6px" width="250px">
                      <Typography variant="body2">{provider.dni}</Typography>
                    </TableCell>
                    <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                      <AbilityTag
                        color={ABILITY_STATUS_TYPES[provider.status].color}
                        enableIcon
                        padding="0"
                        text={ABILITY_STATUS_TYPES[provider.status].label}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </Table>
        </TableWrapper>
      )}

      {!error && completed && !failed && !(dni !== undefined) && (
        <>
          {(opening?.sellers?.length > 0 || opening?.temporaryUnions?.length > 0) && (
            <TableWrapper>
              <Table accessibility={{ label: 'Estado de inscripción' }} loading={loading}>
                <TableRow>
                  <Typography variant="body1" fontWeight="bold" margin="15px 0 40px">
                    Proveedores en apertura
                  </Typography>
                </TableRow>

                {!error &&
                  completed &&
                  !failed &&
                  opening &&
                  Array.isArray(opening.sellers) &&
                  opening.sellers.length > 0 &&
                  (numAcquisition
                    ? opening.sellers?.map((seller, index) => (
                        <Fragment key={`row-${index}`}>
                          <TableRow>
                            <TableCell disableBottomBorder padding="0 9px 6px 0">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                Proveedor
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="0 9px 6px 0">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                Tipo de Proveedor
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="0 9px 6px" width="250px">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                RUT/ID
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                Estado
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell disableBottomBorder padding="6px 9px 36px 0">
                              <Typography variant="body2">{seller.orgLegalName}</Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="6px 9px 36px 0">
                              <Typography variant="body2">{seller.typeSeller}</Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="6px 9px 6px" width="250px">
                              <Typography variant="body2">{seller.dni}</Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                              <AbilityTag
                                color={ABILITY_STATUS_TYPES[seller.ability.status]?.color}
                                enableIcon
                                padding="0"
                                text={ABILITY_STATUS_TYPES[seller.ability.status]?.label}
                              />
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      ))
                    : opening.sellers?.map((seller, index) => (
                        <Fragment key={`row-${index}`}>
                          <TableRow>
                            <TableCell disableBottomBorder padding="0 9px 6px 0">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                Proveedor
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="0 9px 6px 0">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                Tipo de Proveedor
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="0 9px 6px" width="150px">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                RUT/ID
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                              <Typography color="black3" fontWeight="bold" variant="body1">
                                Estado
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell disableBottomBorder padding="6px 9px 36px 0">
                              <Typography fontWeight="bold" variant="body2">
                                {seller.orgLegalName}
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="6px 9px 36px 0">
                              <Typography fontWeight="bold" variant="body2">
                                {seller.typeSeller}
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="6px 9px 6px" width="150px">
                              <Typography fontWeight="bold" variant="body2">
                                {seller.dni}
                              </Typography>
                            </TableCell>
                            <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                              <AbilityTag
                                color={ABILITY_STATUS_TYPES[seller.ability.status]?.color}
                                enableIcon
                                padding="0"
                                text={ABILITY_STATUS_TYPES[seller.ability.status]?.label}
                              />
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      )))}

                {!error &&
                  completed &&
                  !failed &&
                  numAcquisition &&
                  opening &&
                  Array.isArray(opening.temporaryUnions) &&
                  opening.temporaryUnions.length > 0 &&
                  opening.temporaryUnions.map(UTP => (
                    <Fragment key={UTP.idUTP}>
                      <TableRow>
                        <TableCell disableBottomBorder padding="20px 9px 6px 0">
                          <Typography color="black3" fontWeight="bold" variant="body1">
                            UTP
                          </Typography>
                        </TableCell>
                        <TableCell disableBottomBorder padding="0 9px 6px" width="250px">
                          <Typography color="black3" fontWeight="bold" variant="body1">
                            RUT/ID
                          </Typography>
                        </TableCell>
                        <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                          <Typography color="black3" fontWeight="bold" variant="body1">
                            Estado
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {UTP.providers.map(provider => (
                        <TableRow key={provider.dni}>
                          <TableCell disableBottomBorder padding="6px 9px 36px 0">
                            <Typography variant="body2">{provider.orgLegalName.toUpperCase()}</Typography>
                          </TableCell>
                          <TableCell disableBottomBorder padding="6px 9px 6px" width="250px">
                            <Typography variant="body2">{provider.dni}</Typography>
                          </TableCell>
                          <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                            <AbilityTag
                              color={ABILITY_STATUS_TYPES[provider.status]?.color}
                              enableIcon
                              padding="0"
                              text={ABILITY_STATUS_TYPES[provider.status]?.label}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
              </Table>
            </TableWrapper>
          )}

          <TableWrapper>
            <Table accessibility={{ label: 'Estado de inscripción' }} loading={loading}>
              <TableRow>
                <Typography variant="body1" fontWeight="bold" margin="15px 0 0">
                  Proveedores adjudicados
                </Typography>
              </TableRow>
              {!error &&
                completed &&
                !failed &&
                adjudication &&
                Array.isArray(adjudication.sellers) &&
                adjudication.sellers.length > 0 &&
                adjudication.sellers.map((seller, index) => (
                  <Fragment key={`row-${index}`}>
                    <TableRow>
                      <TableCell disableBottomBorder padding="0 9px 6px 0">
                        <Typography color="black3" fontWeight="bold" variant="body1">
                          Proveedor
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="0 9px 6px 0">
                        <Typography color="black3" fontWeight="bold" variant="body1">
                          Tipo de Proveedor
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="0 9px 6px" width="250px">
                        <Typography color="black3" fontWeight="bold" variant="body1">
                          RUT/ID
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                        <Typography color="black3" fontWeight="bold" variant="body1">
                          Estado
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell disableBottomBorder padding="6px 9px 36px 0">
                        <Typography variant="body2">{seller.orgLegalName}</Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="6px 9px 36px 0">
                        <Typography variant="body2">{seller.typeSeller}</Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="6px 9px 6px" width="250px">
                        <Typography variant="body2">{seller.dni}</Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                        <AbilityTag
                          color={ABILITY_STATUS_TYPES[seller.ability.status]?.color}
                          enableIcon
                          padding="0"
                          text={ABILITY_STATUS_TYPES[seller.ability.status]?.label}
                        />
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              {!error &&
                completed &&
                !failed &&
                numAcquisition &&
                adjudication &&
                Array.isArray(adjudication.temporaryUnions) &&
                adjudication.temporaryUnions.length > 0 &&
                adjudication.temporaryUnions.map(UTP => (
                  <Fragment key={UTP.idUTP}>
                    <TableRow>
                      <TableCell disableBottomBorder padding="20px 9px 6px 0">
                        <Typography color="black3" fontWeight="bold" variant="body1">
                          UTP
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="0 9px 6px" width="250px">
                        <Typography color="black3" fontWeight="bold" variant="body1">
                          RUT/ID
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="0 0 6px 9px" width="150px">
                        <Typography color="black3" fontWeight="bold" variant="body1">
                          Estado
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {UTP.providers.map(provider => (
                      <TableRow key={provider.dni}>
                        <TableCell disableBottomBorder padding="6px 9px 36px 0">
                          <Typography variant="body2">{provider.orgLegalName.toUpperCase()}</Typography>
                        </TableCell>
                        <TableCell disableBottomBorder padding="6px 9px 6px" width="250px">
                          <Typography variant="body2">{provider.dni}</Typography>
                        </TableCell>
                        <TableCell disableBottomBorder padding="6px 0 6px 9px" width="150px">
                          <AbilityTag
                            color={ABILITY_STATUS_TYPES[provider.status]?.color}
                            enableIcon
                            padding="0"
                            text={ABILITY_STATUS_TYPES[provider.status]?.label}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              {adjudication &&
                Array.isArray(adjudication.sellers) &&
                adjudication.sellers.length === 0 &&
                Array.isArray(adjudication.temporaryUnions) &&
                adjudication.temporaryUnions.length === 0 && (
                  <TableRowWrapper>
                    <TableCell disableBottomBorder colSpan={3}>
                      <StyledImage
                        alt="Aún no existen proveedores adjudicados para este proceso"
                        src={`${PUBLIC_URL}/assets/images/notProviders.svg`}
                      />
                      <Typography align="center" color="black3" variant="body2">
                        Aún no existen proveedores adjudicados
                        <br />
                        para este proceso
                      </Typography>
                    </TableCell>
                  </TableRowWrapper>
                )}
            </Table>
          </TableWrapper>
        </>
      )}

      {!error &&
        !loading &&
        completed &&
        !failed &&
        disabilityReasons?.length > 0 &&
        disabilityReasons.map((reason, index) => {
          return (
            <AbilityReasonError key={`key-${index}`} display={isTiny && !isSmall ? 'block' : 'flex'}>
              <Content maxWidth="160px" minWidth="230px">
                <Typography variant="body2" fontWeight="bold">
                  <ProtectByFeatures rule={features => features['com.seller/antiunion.practices.visible']}>
                    {reason === 'PRÁCTICAS ANTISINDICALES'
                      ? ANTIUNION_PRACTICES['PRÁCTICAS ANTISINDICALES'].title
                      : DISABILITY_REASONS[reason].title}
                  </ProtectByFeatures>
                  <ProtectByFeatures rule={features => features['com.seller/antiunion.practices.not.visible']}>
                    {DISABILITY_REASONS[reason].title}
                  </ProtectByFeatures>
                </Typography>
                <ProtectByFeatures rule={features => features['com.seller/antiunion.practices.visible']}>
                  <Typography variant="subtitle">
                    {reason === 'PRÁCTICAS ANTISINDICALES'
                      ? ANTIUNION_PRACTICES['PRÁCTICAS ANTISINDICALES'].subtitle
                      : DISABILITY_REASONS[reason].subtitle}
                  </Typography>
                </ProtectByFeatures>
                <ProtectByFeatures rule={features => features['com.seller/antiunion.practices.not.visible']}>
                  <Typography variant="subtitle">{DISABILITY_REASONS[reason].subtitle}</Typography>
                </ProtectByFeatures>
              </Content>
              {!(isTiny && !isSmall) && <ErrorIcon />}
              <ProtectByFeatures rule={features => features['com.seller/antiunion.practices.visible']}>
                <Typography variant="body2">
                  {reason === 'PRÁCTICAS ANTISINDICALES'
                    ? ANTIUNION_PRACTICES['PRÁCTICAS ANTISINDICALES'].negative
                    : DISABILITY_REASONS[reason].negative}
                </Typography>
              </ProtectByFeatures>
              <ProtectByFeatures rule={features => features['com.seller/antiunion.practices.not.visible']}>
                <Typography variant="body2">{DISABILITY_REASONS[reason].negative}</Typography>
              </ProtectByFeatures>
            </AbilityReasonError>
          )
        })}
    </Root>
  )
}

export default EnrollmentCertificateSection
